import { AccessRequest } from '../types';
import { apiGet, apiPost } from './api';

// Loose user shape (the caller may pass the app user or any object); the server
// derives the requester from the session, so fields here are advisory only.
type RequestingUser = { email?: string | null; displayName?: string | null; photoURL?: string | null };

interface RawRequest {
    id: string;
    email: string;
    displayName: string;
    photoURL: string;
    requestedAt: string;
    status: 'pending' | 'approved' | 'denied';
    reviewedAt?: string | null;
    reviewedBy?: string | null;
    notes?: string | null;
}

const revive = (r: RawRequest): AccessRequest => ({
    id: r.id,
    email: r.email,
    displayName: r.displayName,
    photoURL: r.photoURL,
    requestedAt: new Date(r.requestedAt),
    status: r.status,
    reviewedAt: r.reviewedAt ? new Date(r.reviewedAt) : undefined,
    reviewedBy: r.reviewedBy ?? undefined,
    notes: r.notes ?? undefined,
});

/** Submit a new access request for the signed-in user. */
export async function submitAccessRequest(_user: RequestingUser): Promise<void> {
    await apiPost('/access/request');
}

/** Get the signed-in user's existing access request (email arg ignored). */
export async function checkExistingRequest(_email?: string): Promise<AccessRequest | null> {
    const { request } = await apiGet<{ request: RawRequest | null }>('/access/status');
    return request ? revive(request) : null;
}

/** Admin: list access requests, optionally filtered by status. */
export async function getAccessRequests(status?: 'pending' | 'approved' | 'denied'): Promise<AccessRequest[]> {
    const { requests } = await apiGet<{ requests: RawRequest[] }>(
        `/admin/access-requests${status ? `?status=${status}` : ''}`,
    );
    return requests.map(revive);
}

/** Admin: approve an access request. */
export async function approveAccessRequest(requestId: string, _adminEmail: string, notes?: string): Promise<void> {
    await apiPost(`/admin/access-requests/${requestId}/approve`, { notes });
}

/** Admin: deny an access request. */
export async function denyAccessRequest(requestId: string, _adminEmail: string, notes?: string): Promise<void> {
    await apiPost(`/admin/access-requests/${requestId}/deny`, { notes });
}

/** Whether the signed-in user is approved to use the app. */
export async function isUserApproved(_email?: string): Promise<boolean> {
    try {
        const me = await apiGet<{ approved?: boolean }>('/auth/me');
        return !!me.approved;
    } catch (error) {
        console.error('Error checking if user is approved:', error);
        return false;
    }
}
